import { IIngredients, IIngredientsTable } from "../interfaces";

import {
    Icon,
    IconButton,
    InputAdornment,
    Box,
    FormControl,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from "@mui/material";
import { modalHide, modalShow } from "app/App/actions";
import {
    formatUnit,
    pricePerUnit,
    useUnitGroupsSelectOptions,
} from "helpers/units";
import trans from "helpers/trans";
import { isBoolean, isNull, keys } from "lodash";
import { FC, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { Table } from "ui";
import { grey } from "@mui/material/colors";

const IngredientsTable: FC<IIngredientsTable> = ({
    onChange,
    onDelete,
    rows,
}) => {
    const dispatch = useDispatch();
    const itemClickedRef = useRef<any>();
    const unitList = useUnitGroupsSelectOptions();

    const sum = useMemo(() => {
        return rows.reduce((acc, item) => acc + item.cost, 0);
    }, [rows]);

    const data = useMemo(() => {
        if (rows.length === 0) {
            return [];
        }
        return rows.map((item) => {
            let sizeUnit = formatUnit(
                item.ingredient.size,
                item.ingredient.unit,
                item.ingredient.show_unit
            );
            return {
                ...item,
                ingredient: { ...item.ingredient, sizeUnit },
            };
        });
    }, [rows]);

    const replaceSelectedIngredient = (item: any) => {
        dispatch(modalHide("IngredientsSearchModal"));
        if (
            rows.filter((row: any) => row.ingredient_id === item.id).length > 0
        ) {
            alert(trans("inv.ingredients.component.ingredients.alert"));
            return;
        }
        const itemClicked = itemClickedRef.current;

        const newRows = rows.map((row: any) => {
            if (row.ingredient_id === itemClicked.ingredient_id) {
                const quantityWithWaste: number =
                    (itemClicked.waste * Number(itemClicked.quantity)) / 100 +
                    Number(itemClicked.quantity);
                return {
                    ...row,
                    ingredient: item,
                    ingredient_id: item.id,
                    cost:
                        pricePerUnit(
                            item.price,
                            item.size,
                            itemClicked.ingredient.unit,
                            itemClicked.unit
                        ) * quantityWithWaste,
                };
            }
            return row;
        });
        onChange(newRows);
    };

    const updateIngredient = (item: IIngredients, idx: number) => {
        const quantityWithWaste: number =
            (item.waste * Number(item.quantity)) / 100 + Number(item.quantity);

        return onChange([
            ...rows.slice(0, idx),
            {
                ...item,
                cost:
                    pricePerUnit(
                        item.ingredient.price,
                        item.ingredient.size,
                        item.ingredient.unit,
                        item.unit
                    ) * quantityWithWaste,
            },
            ...rows.slice(idx + 1),
        ]);
    };

    const handleBlurQuantity = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        idx: number
    ) => {
        if (e.target.value === "") {
            const item = {
                ...rows[idx],
                quantity: 0,
            };

            updateIngredient(item, idx);
        }
    };
    const handleBlurWaste = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        idx: number
    ) => {
        if (e.target.value === "") {
            const item = {
                ...rows[idx],
                waste: 0,
            };

            updateIngredient(item, idx);
        }
    };

    const handleChangeQuantity = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        idx: number
    ) => {
        let quantity = e.target.value;
        const item = {
            ...rows[idx],
            quantity: quantity as any,
        };
        updateIngredient(item, idx);
    };

    const handleChangeUnit = (e: SelectChangeEvent<string>, idx: number) => {
        let unit = e.target.value;
        const item = {
            ...rows[idx],
            unit,
        };
        updateIngredient(item, idx);
    };
    const handleChangeWaste = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        idx: number
    ) => {
        let waste = e.target.value;
        const item = {
            ...rows[idx],
            waste: waste as any,
        };
        updateIngredient(item, idx);
    };

    const handleIngredientName = (item: any) => {
        itemClickedRef.current = item;
        dispatch(
            modalShow("IngredientsSearchModal", {
                selectIngredient: replaceSelectedIngredient,
            })
        );
    };

    const renderDelete = (idx: number) => {
        return (
            <IconButton onClick={(e) => onDelete(idx)}>
                <Icon>delete</Icon>
            </IconButton>
        );
    };

    const renderQuantityUnit = (item: IIngredients, idx: number) => {
        return (
            <Box sx={{ width: "100%", minWidth: "200px", display: "flex" }}>
                <TextField
                    id="outlined-number"
                    value={item.quantity}
                    type="number"
                    onBlur={(e) => handleBlurQuantity(e, idx)}
                    onChange={(e) =>
                        !e.target.value.includes("-") &&
                        handleChangeQuantity(e, idx)
                    }
                    variant="outlined"
                    fullWidth
                    InputProps={{
                        inputProps: { min: 0 },
                        sx: {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                        },
                    }}
                    size="small"
                />
                <FormControl fullWidth size="small">
                    <Select
                        id="select-unit"
                        onChange={(e) => handleChangeUnit(e, idx)}
                        value={item.unit}
                        sx={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                        }}
                    >
                        {unitList}
                    </Select>
                </FormControl>
            </Box>
        );
    };
    const renderWaste = (item: IIngredients, idx: number) => {
        return (
            <Box sx={{ width: "100%", minWidth: 100, display: "flex" }}>
                <TextField
                    id="waste-input"
                    value={item.waste}
                    type="number"
                    onBlur={(e) => handleBlurWaste(e, idx)}
                    onChange={(e) =>
                        !e.target.value.includes("-") &&
                        handleChangeWaste(e, idx)
                    }
                    variant="outlined"
                    size="small"
                    margin="none"
                    InputProps={{
                        inputProps: { min: 0 },
                        endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                        ),
                    }}
                />
            </Box>
        );
    };
    return (
        <Table
            calculateHeight={false}
            id="IngredientsTable"
            calculateWidth={true}
            columns={[
                {
                    field: "ingredient.name",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.name"
                    ),
                    cellProps: {
                        size: "small",
                        sx: { whiteSpace: "nowrap" },
                    },
                    renderCell: (item, column, idx) => ({
                        children: (
                            <Typography
                                sx={{
                                    borderBottom: `1px dashed ${grey[600]}`,
                                    cursor: "pointer",
                                    fontSize: "0.85rem",
                                    width: "fit-content",
                                }}
                                onClick={() => handleIngredientName(item)}
                            >
                                {item.ingredient.name}
                            </Typography>
                        ),
                    }),
                },
                {
                    field: "ingredient.sizeUnit",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.size"
                    ),
                    cellProps: {
                        align: "right",
                        size: "small",
                        sx: { whiteSpace: "nowrap" },
                    },
                },
                {
                    field: "",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.allergens"
                    ),
                    cellProps: {
                        align: "center",
                        size: "small",
                    },
                    renderCell: (item: IIngredients) => {
                        const allergens = item.ingredient.product.allergens;
                        const filteredAllergens = allergens
                            ? keys(allergens)
                                  .map((k) => allergens[k])
                                  .filter((e) => isBoolean(e) || isNull(e))
                            : [];

                        return {
                            children: filteredAllergens.some((e: any) => e) ? (
                                <Icon
                                    sx={{
                                        display: "block",
                                        margin: "0 auto",
                                        color: "red",
                                    }}
                                    fontSize="small"
                                >
                                    circle
                                </Icon>
                            ) : filteredAllergens.some((e: any) => isNull(e)) ||
                              filteredAllergens.length === 0 ? (
                                <Icon
                                    sx={{
                                        display: "block",
                                        margin: "0 auto",
                                        color: "grey.500",
                                    }}
                                    fontSize="small"
                                >
                                    circle
                                </Icon>
                            ) : (
                                <Icon
                                    sx={{
                                        display: "block",
                                        margin: "0 auto",
                                        color: "green",
                                    }}
                                    fontSize="small"
                                >
                                    circle
                                </Icon>
                            ),
                        };
                    },
                },
                {
                    field: "ingredient.price",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.price"
                    ),
                    cellProps: {
                        align: "right",
                        size: "small",
                        sx: { whiteSpace: "nowrap" },
                    },
                    renderCell: (item) => ({
                        children: item.ingredient.price.price(),
                    }),
                },
                {
                    field: "quantity",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.quantity"
                    ),
                    cellProps: {
                        size: "small",
                    },
                    renderCell: (item, column, idx) => ({
                        children: renderQuantityUnit(item, idx),
                    }),
                },
                {
                    field: "ingredient.waste",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.waste"
                    ),
                    cellProps: {
                        size: "small",
                    },
                    renderCell: (item, column, idx) => ({
                        children: renderWaste(item, idx),
                    }),
                },
                {
                    field: "",
                    headName: trans(
                        "inv.ingredients.table.ingredientsTable.headName.cost"
                    ),
                    cellProps: {
                        align: "right",
                        size: "small",
                        sx: { whiteSpace: "nowrap" },
                    },
                    renderCell: (item) => ({
                        children: item.cost.price(),
                    }),
                },
                {
                    field: "",
                    headName: "",
                    cellProps: {
                        align: "right",
                        size: "small",
                    },
                    renderCell: (item) => ({
                        children:
                            sum !== 0
                                ? `${((item.cost / sum) * 100).toFixed(2)}%`
                                : `${sum.toFixed(2)}%`,
                    }),
                },
                {
                    field: "",
                    headName: "",
                    cellProps: {
                        align: "right",
                        size: "small",
                    },
                    renderCell: (data, column, idx) => ({
                        children: renderDelete(idx),
                    }),
                },
            ]}
            rows={data}
        />
    );
};

export default IngredientsTable;
